/**
 * 路由元信息。
 *
 * `viewMeta` 来自原型 `app.js`（已同步进 `src/data/baseline.json`），
 * 这里只为原型之后新增的视图（分类向导 / 真相层 / 模拟 / 审核 / 导出）补齐元信息，
 * 其余一律读基线，保证与原型文案零漂移。
 */

import baseline from "@/data/baseline.json";

export interface RouteMeta {
  eyebrow: string;
  title: string;
}

type BaselineViewMeta = Record<string, RouteMeta>;

const baselineMeta = baseline.viewMeta as BaselineViewMeta;

/** 原型之后的扩展视图；key 为路由段（studio 子路由）。 */
const EXTENDED_META: Record<string, RouteMeta> = {
  classify: { eyebrow: "项目立项 · 六维分类", title: "项目分类向导" },
  canon: { eyebrow: "S2 · 不可协商的真相", title: "真相层" },
  simulate: { eyebrow: "六类玩家 · 路径与结局覆盖", title: "路径模拟" },
  review: { eyebrow: "G-1..G7 · 未过关不得标记正式", title: "审核关口" },
  export: { eyebrow: "正式版本交付 · 可追溯", title: "导出交付" },
  audit: { eyebrow: "运行 · 规则 · 人工决定", title: "审计追溯" },
  audience: { eyebrow: "观众信号 · 编辑端", title: "观众洞察" },
  system: { eyebrow: "系统观测 · HTTP 与业务口径", title: "运行指标" },
  // 模型配置原在「运行指标」页首，名实不符导致入口不可发现（2026-10-07 迁出）。
  settings: { eyebrow: "本机安装级 · 模型网关", title: "设置" },
};

export function metaFor(view: string): RouteMeta {
  return baselineMeta[view] ?? EXTENDED_META[view] ?? { eyebrow: "未必 Otherwise", title: view };
}

export interface NavItem {
  view: string;
  label: string;
  symbol: string;
  count?: number;
  alert?: boolean;
}

/**
 * 工作台主导航。
 * 前 6 项沿用原型（`app.js` 侧栏）的顺序与图标；后 6 项为 specs/07 §4 新增视图，
 * 复用同一套 `.nav-item` 基元，因此视觉与交互态保持一致。
 */
export const STUDIO_NAV: NavItem[] = [
  { view: "overview", label: "项目总览", symbol: "▦" },
  { view: "classify", label: "项目分类向导", symbol: "◈" },
  { view: "canon", label: "真相层", symbol: "❖" },
  { view: "story", label: "故事结构", symbol: "⌁", count: 3 },
  { view: "script", label: "正式脚本", symbol: "¶" },
  { view: "checks", label: "结构检查", symbol: "✓", count: 4, alert: true },
  { view: "simulate", label: "路径模拟", symbol: "⇄" },
  { view: "generate", label: "生成中心", symbol: "◇" },
  { view: "review", label: "审核关口", symbol: "✔" },
  { view: "export", label: "导出交付", symbol: "↥" },
  { view: "growth", label: "编剧成长", symbol: "↗" },
  { view: "audit", label: "审计追溯", symbol: "⌖" },
  { view: "audience", label: "观众洞察", symbol: "◍" },
  { view: "system", label: "运行指标", symbol: "◔" },
  // 安装级配置，不属于任何项目 ⇒ 置于项目视图之后。
  // `⚙︎` 带 U+FE0E（文本呈现），避免被渲染成彩色 emoji 而破坏这套单色符号的一致性。
  { view: "settings", label: "设置", symbol: "⚙︎" },
];

export interface PlatformNavItem {
  view: string;
  to: string;
  label: string;
}

/** 平台一级入口；`creator` 指向最近一次工作台视图。 */
export const PLATFORM_NAV: PlatformNavItem[] = [
  { view: "home", to: "/", label: "首页" },
  { view: "discover", to: "/discover", label: "故事广场" },
  { view: "creator", to: "/studio/overview", label: "专业创作台" },
  { view: "community", to: "/community", label: "共创社区" },
];
